import React, { useMemo } from "react";
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip, Legend } from "recharts";
import EmptyState from "./EmptyState";
import { TOPIC_CLUSTERS } from "../constants/topicClusters";
import TooltipUI from "./Tooltip";
import InfoIcon from "./InfoIcon";

const clusterDefinitions = TOPIC_CLUSTERS.map(({ id, name, color, description }) => ({
  id,
  name,
  color,
  description
}));

const TopicDistributionChart = ({ data }) => {

  // Transform time-series data into distribution data
  const chartData = useMemo(() => {
    if (!data || data.length === 0) {
      // Return default empty data structure
      return clusterDefinitions.map(cluster => ({
        name: cluster.name,
        value: 0,
        color: cluster.color
      }));
    }

    // Aggregate all values across time for each cluster
    const aggregatedData = clusterDefinitions.map(cluster => {
      const totalValue = data.reduce((sum, timePoint) => {
        return sum + (timePoint[cluster.id] || 0);
      }, 0);

      return {
        name: cluster.name,
        value: totalValue,
        color: cluster.color
      };
    });

    // Filter out clusters with zero values for cleaner display
    return aggregatedData.filter(item => item.value > 0);
  }, [data]);

  // Check if we have actual data
  if (!data || data.length === 0 || chartData.length === 0) {
    return (
      <div className="bg-white-f p-5 rounded-lg border border-grey-20 shadow-subtle">
        <div className="flex items-center gap-1 mb-4">
          <h3 className="text-sm font-normal text-black-0">Topic distribution</h3>
          <TooltipUI
            content="Shows how conversation topics are distributed across the 5 clusters for the selected period."
            placement="bottom"
            align="start"
          >
            <span className="inline-flex cursor-help">
              <InfoIcon />
            </span>
          </TooltipUI>
        </div>
        <EmptyState
          variant="no-data"
          title="No topic data available"
          description="There's no topic distribution data for the selected period. Try selecting a different date range."
          actionButton={null}
        />
      </div>
    );
  }

  // Calculate total and most frequent topic
  const total = chartData.reduce((sum, item) => sum + item.value, 0);

  const getMostFrequentTopic = () => {
    if (!chartData || chartData.length === 0 || total === 0) {
      return { name: "No data", color: "#ABABBA" };
    }
    
    const maxItem = chartData.reduce((max, current) => 
      current.value > max.value ? current : max
    );
    
    return maxItem;
  };

  const mostFrequentTopic = getMostFrequentTopic();

  // Custom tooltip
  const CustomTooltip = ({ active, payload }) => {
    if (active && payload && payload.length) {
      const item = payload[0].payload;
      const percentage = total > 0 ? Math.round((item.value / total) * 100) : 0;
      
      return (
        <div className="bg-white-f p-3 border border-grey-20 rounded-md shadow-subtle">
          <p className="text-sm font-medium" style={{ color: item.color }}>{item.name}</p>
          <p className="text-sm text-grey-60">
            <span className="font-medium">{percentage}%</span>
          </p>
        </div>
      );
    }
    return null;
  };

  // Custom legend
  const CustomLegend = ({ payload }) => {
    return (
      <div className="flex justify-center items-center flex-wrap gap-x-4 gap-y-2 mt-4 px-2">
        {payload.map((entry, index) => (
          <div key={`legend-${index}`} className="flex items-center flex-shrink-0">
            <div 
              className="w-2 h-2 rounded-full mr-1 flex-shrink-0" 
              style={{ backgroundColor: entry.color }}
            ></div>
            <span className="text-xs text-grey-60 whitespace-nowrap">{entry.value}</span>
          </div>
        ))}
      </div>
    );
  };

  return (
    <div className="bg-white-f border border-grey-20 shadow-subtle rounded-lg px-5 py-4 flex flex-col w-[360px] flex-1 min-h-0">
      <div className="flex flex-col gap-4 flex-1 min-h-0">
        <div className="flex items-center">
          <p className="text-sm font-normal text-black-0 tracking-[-0.5px]">
            Cluster distribution
          </p>
        </div>

        <div className="flex-1 flex items-center justify-center min-h-0">
          <div className="w-[160px] h-[160px]">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie
                  data={chartData}
                  cx="50%"
                  cy="50%"
                  innerRadius={60}
                  outerRadius={78}
                  paddingAngle={2}
                  dataKey="value"
                >
                  {chartData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry.color} />
                  ))}
                </Pie>
                <Tooltip content={<CustomTooltip />} />
              </PieChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div className="flex justify-center">
          <CustomLegend
            payload={chartData.map((item) => ({ value: item.name, color: item.color }))}
          />
        </div>
      </div>
    </div>
  );
};

export default TopicDistributionChart; 